import React from 'react';

import { DEFAULT_KPI_ICON, KPI_ICONS } from 'assets/svg/landing/kpiIcons';
import { LandingTrendArrow } from 'assets/svg/landing/landingIcons';
import {
    rgmExecSummaryCardTestId,
    rgmExecSummaryDeltaTestId,
    rgmExecSummaryValueTestId,
} from 'constants/landingTestIds';
import { ExecutiveSummaryCardDto, ExecutiveSummarySentiment } from 'types/landing';

interface Props {
    card: ExecutiveSummaryCardDto;
}

/**
 * Chip colours track `sentiment`, not `direction` — an inverse KPI falling is a good result and
 * must read green even though its arrow points down. See docs/api/landing-bff-contract.md.
 */
const SENTIMENT_CLASSES: Record<ExecutiveSummarySentiment, string> = {
    positive: 'bg-landingTrendUpBg text-landingTrendUp',
    negative: 'bg-validationErrorBg text-validationErrorDot',
    neutral: 'bg-filterV2Border text-filterV2TextSecondary',
};

const formatDeltaValue = (value: number, unit: string): string => `${value}${unit === 'percent' ? '%' : ''}`;

export const CARD_SHELL_CLASSNAME =
    'flex min-w-px flex-[1_0_0] flex-col rounded-[12px] border border-landingCardBorder bg-white px-5 py-3';

function ExecutiveSummaryCard({ card }: Props) {
    const { Icon, bgClassName, hoverBorderClassName } = (card.icon && KPI_ICONS[card.icon]) || DEFAULT_KPI_ICON;
    const { delta } = card;
    const displayValue = card.display_value ?? String(card.value);

    return (
        <div
            data-testid={rgmExecSummaryCardTestId(card.id)}
            className={`${CARD_SHELL_CLASSNAME} transition-[box-shadow,border-color] duration-200 ${hoverBorderClassName} hover:shadow-landingCardHover`}
        >
            <div className="flex w-full flex-col gap-[7px]">
                <div className="flex w-full flex-col gap-2">
                    <div className="flex w-full items-center justify-between gap-2">
                        <p className="truncate font-sm font-normal leading-[22px] text-filterV2TextSecondary">
                            {card.label}
                        </p>
                        <span className={`flex size-6 shrink-0 items-center justify-center rounded ${bgClassName}`}>
                            <Icon className="size-4" />
                        </span>
                    </div>
                    <p
                        data-testid={rgmExecSummaryValueTestId(card.id)}
                        className="w-full truncate text-[28px] font-bold leading-[38px] text-filterV2TextPrimary"
                    >
                        {card.value_prefix ?? ''}
                        {displayValue}
                        {card.value_suffix ?? ''}
                    </p>
                </div>

                {delta && (
                    <div
                        data-testid={rgmExecSummaryDeltaTestId(card.id)}
                        className={`flex h-5 w-fit items-center gap-1 rounded px-1 ${
                            SENTIMENT_CLASSES[delta.sentiment]
                        }`}
                    >
                        {delta.direction !== 'flat' && (
                            <LandingTrendArrow className="size-3 shrink-0" isUp={delta.direction === 'up'} />
                        )}
                        <p className="whitespace-nowrap font-xxs leading-[18px]">
                            <span className="font-medium">{formatDeltaValue(delta.value, delta.unit)} </span>
                            <span className="font-normal text-filterV2TextSecondary">{delta.comparison_label}</span>
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ExecutiveSummaryCard;
